import { useEffect } from 'react';

const DEFAULT_SITE_NAME = 'ALIEN GREEN (EduTechnologies)';
const DEFAULT_DESCRIPTION = 'ALIEN GREEN offers preparation for End of Semester exams, Licensing exams, and CAT 1/CAT 2 exams with well-organized study materials by programme, module, and topic, plus self-evaluation tests.';
const DEFAULT_KEYWORDS = [
  'ALIEN GREEN',
  'ALIEN GREEN EduTechnologies',
  'pharmacy exam preparation Tanzania',
  'licensing exam preparation Tanzania',
  'end of semester exam preparation',
  'CAT 1 exam preparation',
  'CAT 2 exam preparation',
  'medical study materials Tanzania',
  'pharmacy short courses Tanzania',
  'self evaluation tests',
  'programme module topic learning',
];
const DEFAULT_IMAGE_PATH = '/pwa-icon.png';
const DEFAULT_SITE_URL = 'https://aliengreen.pharmagreen.co.tz';

function buildDefaultJsonLd(siteUrl) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: DEFAULT_SITE_NAME,
        url: siteUrl,
        logo: `${siteUrl}${DEFAULT_IMAGE_PATH}`,
        description: DEFAULT_DESCRIPTION,
        areaServed: 'TZ',
        sameAs: [],
      },
      {
        '@type': 'WebSite',
        name: DEFAULT_SITE_NAME,
        url: siteUrl,
        description: DEFAULT_DESCRIPTION,
        inLanguage: ['en', 'sw'],
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/short-courses`,
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };
}

function upsertMeta(name, content, useProperty = false) {
  const selector = useProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    if (useProperty) {
      tag.setAttribute('property', name);
    } else {
      tag.setAttribute('name', name);
    }
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertCanonical(url) {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', url);
}

function upsertJsonLd(data) {
  const id = 'seo-json-ld';
  let tag = document.head.querySelector(`#${id}`);
  if (!data) {
    if (tag) {
      tag.remove();
    }
    return;
  }
  if (!tag) {
    tag = document.createElement('script');
    tag.id = id;
    tag.type = 'application/ld+json';
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  imagePath = DEFAULT_IMAGE_PATH,
  type = 'website',
  noIndex = false,
  keywords = [],
  jsonLd = null,
}) {
  useEffect(() => {
    const siteUrl = (
      process.env.REACT_APP_SITE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : DEFAULT_SITE_URL)
    ).replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const canonicalUrl = `${siteUrl}${normalizedPath}`;
    const imageUrl = imagePath.startsWith('http') ? imagePath : `${siteUrl}${imagePath}`;
    const fullTitle = title ? `${title} | ${DEFAULT_SITE_NAME}` : DEFAULT_SITE_NAME;

    document.title = fullTitle;
    upsertCanonical(canonicalUrl);

    upsertMeta('description', description);
    upsertMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('theme-color', '#0f172a');
    upsertMeta('author', DEFAULT_SITE_NAME);
    upsertMeta('geo.region', 'TZ');

    upsertMeta('keywords', (keywords.length > 0 ? keywords : DEFAULT_KEYWORDS).join(', '));

    upsertMeta('og:type', type, true);
    upsertMeta('og:site_name', DEFAULT_SITE_NAME, true);
    upsertMeta('og:title', fullTitle, true);
    upsertMeta('og:description', description, true);
    upsertMeta('og:url', canonicalUrl, true);
    upsertMeta('og:image', imageUrl, true);
    upsertMeta('og:locale', 'en_TZ', true);

    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', fullTitle);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', imageUrl);

    upsertJsonLd(jsonLd || (normalizedPath === '/' ? buildDefaultJsonLd(siteUrl) : null));
  }, [description, imagePath, jsonLd, keywords, noIndex, path, title, type]);

  return null;
}
